<script setup lang="ts">
import {
  tpgIconMoreHoriz,
  tpgIconSymbols,
} from '@tpg/grammar-web-icons';
import { onClickOutside } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';

import type { TpgSelectEvent } from '@nora-monorepo/grammar-ui';
import {
  GrammarMenu,
  GrammarMenuItem,
  GrammarSectionHeader,
  GrammarTertiaryButton,
  GrammarText,
} from '@nora-monorepo/grammar-ui';
import type { SymbolDTO } from '@nora-monorepo/nora-api-client';
import { DrawToolType, SymbolUsageEnum } from '@nora-monorepo/nora-api-client';

import SymbolPreview from '#nora-ui/map/components/drawtools/symbol-tool/Symbol.vue';
import { SymbolCompletionBehavior } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool.types';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useSymbolStore } from '#nora-ui/stores/symbol-store';

interface Props {
  mapId: string;
  listView?: boolean;
  menuView?: boolean;
}

const props = defineProps<Props>();

const symbolStore = useSymbolStore();
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const drawToolUI = map.drawToolUI;
const menu = ref<HTMLElement | null>(null);
const menuTrigger = ref<HTMLElement | null>(null);
const menuOpen = ref(false);
const favoriteSymbols = ref<SymbolDTO[]>([]);

const hasFavoriteSymbols = computed(() => {
  return favoriteSymbols.value.length > 0;
});

async function loadFavoriteSymbols() {
  try {
    favoriteSymbols.value = await symbolStore.getFavouriteSymbols(SymbolUsageEnum.MARKER);
  }
  catch (error) {
    console.warn('Failed to load favorite symbols', error);
    favoriteSymbols.value = [];
  }
}

function selectMoreSymbols() {
  drawToolUI.closeToolList();
  drawToolUI.setDrawToolByType(DrawToolType.symbol);
}

function getCompletionBehavior(): SymbolCompletionBehavior {
  if (props.menuView) {
    return SymbolCompletionBehavior.Close;
  }

  if (props.listView) {
    return SymbolCompletionBehavior.ReopenToolList;
  }

  return SymbolCompletionBehavior.ReopenSymbolPicker;
}

function onFavoriteSymbolSelected(symbol: SymbolDTO) {
  drawToolUI.closeToolList();
  drawToolUI.setDrawToolByType(DrawToolType.symbol, {
    symbolPackId: symbol.symbolPackId,
    symbolId: symbol.id,
    symbolName: symbol.name,
    completionBehavior: getCompletionBehavior(),
  });
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function onSelectFavoriteSymbol({ value }: TpgSelectEvent) {
  const symbol = favoriteSymbols.value.find(favoriteSymbol => favoriteSymbol.id === value);

  if (!symbol) {
    return;
  }

  menuOpen.value = false;
  onFavoriteSymbolSelected(symbol);
}

onClickOutside(
  menu,
  () => {
    menuOpen.value = false;
  },
  {
    ignore: [menuTrigger],
  },
);

onMounted(async () => {
  await loadFavoriteSymbols();
});
</script>

<template>
  <div
    v-if="listView"
    class="flex flex-col"
  >
    <GrammarSectionHeader
      :label="hasFavoriteSymbols ? 'Quick access symbols' : 'No quick access symbols selected'"
      header-type="form-header"
    />

    <div class="grid grid-cols-5 gap-2 justify-center pt-1">
      <button
        v-for="symbol in favoriteSymbols"
        :key="symbol.id"
        :title="symbol.name"
        class="flex overflow-hidden items-center rounded-sm border border-light-separator-no-transparency dark:border-primary-500 justify-center text-center w-10 h-10 cursor-pointer"
        @click="onFavoriteSymbolSelected(symbol)"
      >
        <SymbolPreview
          :symbol="symbol"
          class="w-full h-full object-contain"
        />
      </button>
      <GrammarTertiaryButton
        :icon-template="hasFavoriteSymbols ? tpgIconMoreHoriz : tpgIconSymbols"
        @click="selectMoreSymbols()"
      />
    </div>
  </div>
  <div
    v-if="menuView"
    class="relative"
  >
    <div
      ref="menuTrigger"
    >
      <GrammarTertiaryButton
        :icon-template="tpgIconSymbols"
        @click="toggleMenu"
      />
    </div>

    <GrammarMenu
      ref="menu"
      :is-open="menuOpen"
      class="absolute top-full mt-1 z-10"
      @select="onSelectFavoriteSymbol"
    >
      <GrammarMenuItem
        v-if="favoriteSymbols.length === 0"
        disabled
        label="No quick access symbols selected"
      />

      <template v-else>
        <GrammarMenuItem
          v-for="symbol in favoriteSymbols"
          :key="symbol.id"
          :value="symbol.id"
        >
          <template #content>
            <div class="flex flex-row items-center gap-2">
              <SymbolPreview
                :symbol="symbol"
                class="w-7 h-7"
              />
              <GrammarText :text="symbol.name" />
            </div>
          </template>
        </GrammarMenuItem>
      </template>
    </GrammarMenu>
  </div>
</template>
